(function () {
  if (window.FMCPTabs && window.FMCPTabs.initialized) return;

  const state = {
    abortController: null,
    currentCollectionPath: normalizePath(window.location.pathname),
  };
  const parser = new DOMParser();
  const sectionMarkers = ['.breadcrumb-wrap', '.collection-title-only', '.collection-hero', '#ProductGridContainer'];
  const pageCache = new Map();
  const prefetchPromises = new Map();
  const CACHE_LIMIT = 24;

  window.FMCPTabs = { initialized: true };

  function normalizePath(pathname) {
    if (!pathname) return '/';
    if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
    return pathname;
  }

  function isCollectionPath(pathname) {
    return /(^|\/)collections\/[^/]+\/?$/.test(pathname);
  }

  function buildCacheKey(urlObj) {
    return `${normalizePath(urlObj.pathname)}${urlObj.search}`;
  }

  function cachePage(urlObj, html) {
    const key = buildCacheKey(urlObj);
    if (pageCache.has(key)) pageCache.delete(key);
    pageCache.set(key, { html, cachedAt: Date.now() });

    if (pageCache.size > CACHE_LIMIT) {
      const firstKey = pageCache.keys().next().value;
      pageCache.delete(firstKey);
    }
  }

  function getCachedHtml(urlObj) {
    const key = buildCacheKey(urlObj);
    const cached = pageCache.get(key);
    return cached ? cached.html : null;
  }

  function shouldSkipPrefetch() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return false;
    if (connection.saveData) return true;
    if (connection.effectiveType && /(^|-)2g$/.test(connection.effectiveType)) return true;
    return false;
  }

  // Prefetch only on existing mouseover, touchstart or keyboard focus intent.
  // Clicking still uses the same cache/promise/fetch navigation path.
  function prefetchCollection(url) {
    const urlObj = new URL(url, window.location.origin);
    if (urlObj.origin !== window.location.origin) return;
    if (!isCollectionPath(urlObj.pathname)) return;
    if (shouldSkipPrefetch()) return;

    const key = buildCacheKey(urlObj);
    if (pageCache.has(key) || prefetchPromises.has(key)) return;

    const prefetchPromise = fetch(urlObj.toString(), { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`PREFETCH_HTTP_${response.status}`);
        return response.text();
      })
      .then((html) => {
        cachePage(urlObj, html);
        return html;
      })
      .catch(() => null)
      .finally(() => {
        prefetchPromises.delete(key);
      });

    prefetchPromises.set(key, prefetchPromise);
  }

  function hasSpaEnabledTabs() {
    return Boolean(document.querySelector('[data-fmcp-tabs-root][data-fmcp-spa-enabled="true"]'));
  }

  function setLoading(loading) {
    document.querySelectorAll('[data-fmcp-tabs-root]').forEach((root) => {
      root.dataset.fmcpLoading = loading ? 'true' : 'false';
    });
  }

  function updateActiveTabs(targetUrl) {
    const targetPath = normalizePath(new URL(targetUrl, window.location.origin).pathname);
    document.querySelectorAll('[data-fmcp-collection-link]').forEach((link) => {
      const linkPath = normalizePath(new URL(link.href, window.location.origin).pathname);
      const isActive = linkPath === targetPath;
      link.classList.toggle('fmcp-tabs__item--active', isActive);
      if (isActive) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  }

  function updateTrackFade(track) {
    const tabs = track.closest('.fmcp-tabs');
    if (!tabs) return;

    const canCenter = track.scrollWidth <= track.clientWidth + 1;
    track.dataset.fmcpCanCenter = canCenter ? 'true' : 'false';

    if (canCenter) {
      tabs.dataset.fmcpAtEnd = 'true';
      return;
    }

    const atEnd = Math.ceil(track.scrollLeft + track.clientWidth) >= track.scrollWidth - 1;
    tabs.dataset.fmcpAtEnd = atEnd ? 'true' : 'false';
  }

  function bindTrack(track) {
    if (track.dataset.fmcpTrackBound === 'true') {
      updateTrackFade(track);
      return;
    }

    track.dataset.fmcpTrackBound = 'true';
    track.addEventListener(
      'scroll',
      () => {
        updateTrackFade(track);
      },
      { passive: true }
    );
    updateTrackFade(track);
  }

  function bindTracks(scope) {
    scope.querySelectorAll('[data-fmcp-tabs-track]').forEach(bindTrack);
  }

  function replaceSectionByMarker(marker, nextDoc) {
    const currentMarker = document.querySelector(marker);
    const nextMarker = nextDoc.querySelector(marker);
    if (!currentMarker || !nextMarker) return false;

    const currentSection = currentMarker.closest('.shopify-section');
    const nextSection = nextMarker.closest('.shopify-section');
    if (!currentSection || !nextSection) return false;

    currentSection.replaceWith(nextSection);
    return true;
  }

  function replaceCollectionContent(nextDoc) {
    let replacedSomething = false;
    sectionMarkers.forEach((marker) => {
      if (replaceSectionByMarker(marker, nextDoc)) replacedSomething = true;
    });

    const currentTabs = document.querySelector('[data-fmcp-tabs-root]');
    const nextTabs = nextDoc.querySelector('[data-fmcp-tabs-root]');
    if (currentTabs && nextTabs) {
      const links = (root) => Array.from(root.querySelectorAll('[data-fmcp-collection-link]'), (link) => link.getAttribute('href')).join('|');
      if (links(currentTabs) !== links(nextTabs)) {
        replaceSectionByMarker('[data-fmcp-tabs-root]', nextDoc);
      }
    }

    if (replacedSomething) return true;

    const currentMain = document.getElementById('MainContent');
    const nextMain = nextDoc.getElementById('MainContent');
    if (!currentMain || !nextMain) return false;

    currentMain.innerHTML = nextMain.innerHTML;
    return true;
  }

  function buildHistoryState(nextUrl) {
    return {
      fmcpTabs: true,
      searchParams: nextUrl.search.slice(1),
    };
  }

  async function navigateToCollection(url, pushState) {
    const nextUrl = new URL(url, window.location.origin);
    const nextPath = normalizePath(nextUrl.pathname);
    if (!isCollectionPath(nextPath)) {
      window.location.assign(nextUrl.toString());
      return;
    }

    const cacheKey = buildCacheKey(nextUrl);
    const cachedHtml = getCachedHtml(nextUrl);

    if (state.abortController) state.abortController.abort();
    const controller = new AbortController();
    state.abortController = controller;
    if (!cachedHtml) setLoading(true);

    try {
      let html = cachedHtml;
      if (!html) {
        const prefetched = prefetchPromises.get(cacheKey);
        if (prefetched) html = await prefetched;
      }

      if (!html) {
        const response = await fetch(nextUrl.toString(), {
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP_${response.status}`);
        html = await response.text();
      }

      if (controller.signal.aborted) return;
      cachePage(nextUrl, html);

      const nextDoc = parser.parseFromString(html, 'text/html');
      const swapped = replaceCollectionContent(nextDoc);
      if (!swapped) throw new Error('NO_CONTENT_TO_SWAP');

      if (nextDoc.title) document.title = nextDoc.title;
      if (pushState) {
        const historyUrl = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
        window.history.pushState(buildHistoryState(nextUrl), '', historyUrl);
      }

      state.currentCollectionPath = nextPath;
      updateActiveTabs(nextUrl.toString());
      bindTracks(document);
      document.dispatchEvent(new CustomEvent('fmcp:collection-switched', { detail: { url: nextUrl.toString() } }));
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      window.location.assign(nextUrl.toString());
    } finally {
      if (state.abortController === controller) {
        state.abortController = null;
        setLoading(false);
      }
    }
  }

  function isModifiedClick(event) {
    return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-fmcp-collection-link]');
    if (!link) return;

    const root = link.closest('[data-fmcp-tabs-root]');
    if (!root || root.dataset.fmcpSpaEnabled !== 'true') return;
    if (event.defaultPrevented || isModifiedClick(event)) return;
    if (link.target && link.target !== '_self') return;

    const targetUrl = new URL(link.href, window.location.origin);
    if (targetUrl.origin !== window.location.origin) return;
    if (!isCollectionPath(window.location.pathname) || !isCollectionPath(targetUrl.pathname)) return;

    const targetPath = normalizePath(targetUrl.pathname);
    if (targetPath === state.currentCollectionPath && targetUrl.search === window.location.search) return;

    event.preventDefault();
    navigateToCollection(targetUrl.toString(), true);
  });

  document.addEventListener(
    'mouseover',
    (event) => {
      const link = event.target.closest('[data-fmcp-collection-link]');
      if (!link) return;
      prefetchCollection(link.href);
    },
    { passive: true }
  );

  document.addEventListener(
    'touchstart',
    (event) => {
      const link = event.target.closest('[data-fmcp-collection-link]');
      if (!link) return;
      prefetchCollection(link.href);
    },
    { passive: true }
  );

  document.addEventListener('focusin', (event) => {
    const link = event.target.closest('[data-fmcp-collection-link]');
    if (!link) return;
    prefetchCollection(link.href);
  });

  window.addEventListener('popstate', () => {
    if (!hasSpaEnabledTabs()) return;
    if (!isCollectionPath(window.location.pathname)) return;

    const nextPath = normalizePath(window.location.pathname);
    if (nextPath === state.currentCollectionPath) {
      updateActiveTabs(window.location.href);
      return;
    }

    navigateToCollection(window.location.href, false);
  });

  document.addEventListener('shopify:section:load', (event) => {
    bindTracks(event.target);
    updateActiveTabs(window.location.href);
  });

  window.addEventListener('resize', () => {
    bindTracks(document);
  });

  bindTracks(document);
  updateActiveTabs(window.location.href);
})();
